/**
 * Authenticated workspace shell: session probe, menu navigation and routed Outlet.
 */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";

import {
  CreateInternalCaseApiClient,
  InternalApiError,
} from "@libro-reclamos/api-client/InternalCases";
import type { CompanyProfile } from "@libro-reclamos/contracts/Platform";
import {
  HasPlanFeature,
  type CompanyEntitlements,
  type PlanFeatureCode,
} from "@libro-reclamos/contracts/Plans";

import { LoadBackofficeEnvironment } from "../../../config/BackofficeEnvironment";
import { ActionButton } from "../../cases/components/atoms/ActionButton";
import { PendingProfileSummary } from "../../company-profile/CompanyProfilePending";
import { LoginPanel } from "../../cases/components/molecules/LoginPanel";
import { WorkspaceProvider } from "../WorkspaceContext";

const NotificationBell = lazy(async () => {
  const module = await import("../../notifications/components/organisms/NotificationBell");
  return { default: module.NotificationBell };
});

type SessionPhase = "checking" | "authenticated" | "anonymous" | "unavailable";

type WorkspaceSection =
  | "book-notice"
  | "case-reasons"
  | "company"
  | "home"
  | "cases"
  | "follow-ups"
  | "notifications"
  | "artifacts"
  | "tags"
  | "users"
  | "escalations"
  | "platform"
  | "plans";

interface MenuItem {
  readonly adminOnly?: boolean;
  /** Shown only to the administrator of the active company, who owns its profile. */
  readonly companyAdminOnly?: boolean;
  readonly description: string;
  /**
   * Capability the company's plan must include. An item without one is part of
   * every plan, because it is either the claims queue itself or operator-only.
   */
  readonly feature?: PlanFeatureCode;
  readonly label: string;
  readonly platformOnly?: boolean;
  /**
   * Whether the simplified console keeps the item in the primary navigation.
   * Anything else the plan does include moves under "Mi cuenta", so the smallest
   * plan opens on three choices instead of a wall of sections.
   */
  readonly primaryWhenSimplified?: boolean;
  readonly section: WorkspaceSection;
  readonly to:
    | "/artifacts"
    | "/book-notice"
    | "/case-reasons"
    | "/cases"
    | "/company"
    | "/escalations"
    | "/follow-ups"
    | "/notifications"
    | "/platform/companies"
    | "/platform/plans"
    | "/tags"
    | "/users"
    | "/";
}

const MenuItems: readonly MenuItem[] = Object.freeze([
  {
    description: "Resumen general",
    label: "Inicio",
    primaryWhenSimplified: true,
    section: "home",
    to: "/",
  },
  {
    description: "Bandeja operativa",
    label: "Casos",
    primaryWhenSimplified: true,
    section: "cases",
    to: "/cases",
  },
  {
    description: "Recordatorios internos",
    feature: "FOLLOW_UPS",
    label: "Seguimiento",
    section: "follow-ups",
    to: "/follow-ups",
  },
  {
    description: "Avisos recuperables",
    feature: "NOTIFICATIONS",
    label: "Notificaciones",
    section: "notifications",
    to: "/notifications",
  },
  {
    description: "Catálogo para clasificar casos",
    feature: "TAGS",
    label: "Etiquetas",
    section: "tags",
    to: "/tags",
  },
  {
    description: "Categorías, motivos y submotivos",
    feature: "CASE_REASONS",
    label: "Motivos",
    section: "case-reasons",
    to: "/case-reasons",
  },
  {
    description: "Cartel con QR para el local y la web",
    feature: "BOOK_NOTICE",
    label: "Aviso del libro",
    primaryWhenSimplified: true,
    section: "book-notice",
    to: "/book-notice",
  },
  {
    adminOnly: true,
    description: "Alta de cuentas internas",
    feature: "USER_ADMINISTRATION",
    label: "Usuarios",
    section: "users",
    to: "/users",
  },
  {
    adminOnly: true,
    description: "A quién se avisa y cuándo",
    feature: "ESCALATIONS",
    label: "Escalera de avisos",
    section: "escalations",
    to: "/escalations",
  },
  {
    description: "Funciones administrativas",
    feature: "REPORTS",
    label: "Reportes e importaciones",
    section: "artifacts",
    to: "/artifacts",
  },
  {
    companyAdminOnly: true,
    description: "Datos, privacidad y marca",
    label: "Mi empresa",
    section: "company",
    to: "/company",
  },
  {
    description: "Alta y configuración de clientes",
    label: "Empresas",
    platformOnly: true,
    section: "platform",
    to: "/platform/companies",
  },
  {
    description: "Límites y capacidades por plan",
    label: "Planes",
    platformOnly: true,
    section: "plans",
    to: "/platform/plans",
  },
]);

const TitleBySection: Readonly<Record<WorkspaceSection, string>> = Object.freeze({
  artifacts: "Reportes e importaciones",
  "book-notice": "Aviso del Libro de Reclamaciones",
  "case-reasons": "Motivos y categorías",
  cases: "Gestión de casos",
  company: "Mi empresa",
  escalations: "Escalera de avisos",
  "follow-ups": "Seguimiento",
  home: "Inicio",
  notifications: "Notificaciones",
  platform: "Empresas",
  plans: "Planes y capacidades",
  tags: "Etiquetas",
  users: "Usuarios",
});

function SectionFromPathname(pathname: string): WorkspaceSection {
  if (pathname === "/" || pathname === "") {
    return "home";
  }
  if (pathname.startsWith("/case-reasons")) {
    return "case-reasons";
  }
  if (pathname.startsWith("/company")) {
    return "company";
  }
  if (pathname.startsWith("/cases")) {
    return "cases";
  }
  if (pathname.startsWith("/follow-ups")) {
    return "follow-ups";
  }
  if (pathname.startsWith("/notifications")) {
    return "notifications";
  }
  if (pathname.startsWith("/tags")) {
    return "tags";
  }
  if (pathname.startsWith("/book-notice")) {
    return "book-notice";
  }
  if (pathname.startsWith("/escalations")) {
    return "escalations";
  }
  if (pathname.startsWith("/users")) {
    return "users";
  }
  if (pathname.startsWith("/artifacts")) {
    return "artifacts";
  }
  if (pathname.startsWith("/platform/plans")) {
    return "plans";
  }
  if (pathname.startsWith("/platform")) {
    return "platform";
  }
  return "home";
}

function SessionProbeMessage(error: unknown): string {
  if (error instanceof InternalApiError && error.status === 401) {
    return "Inicia sesión para continuar.";
  }
  return "No se pudo conectar con el servicio. Comprueba tu conexión y vuelve a intentarlo.";
}

/**
 * Keeps credentials in memory, exposes a responsive menu and routes one business section at a time.
 */
export function BackofficeWorkspacePage() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const section = SectionFromPathname(pathname);
  const environment = useMemo(() => LoadBackofficeEnvironment(), []);
  const client = useMemo(
    () =>
      CreateInternalCaseApiClient({
        baseUrl: environment.apiBaseUrl,
        timeoutMs: environment.apiTimeoutMs,
      }),
    [environment],
  );
  const [phase, setPhase] = useState<SessionPhase>("checking");
  const [message, setMessage] = useState<string | null>(null);
  const [realtimeMessage, setRealtimeMessage] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [isPlatformOperator, setIsPlatformOperator] = useState(false);
  const [entitlements, setEntitlements] = useState<CompanyEntitlements | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null | undefined>(
    undefined,
  );
  // The setup prompt opens by itself once per sign-in; after that the banner is enough.
  const profilePromptedRef = useRef(false);

  const visibleMenuItems = useMemo(
    () =>
      MenuItems.filter(
        (item) =>
          (item.adminOnly !== true || canManageUsers) &&
          (item.platformOnly !== true || isPlatformOperator) &&
          (item.companyAdminOnly !== true ||
            (companyProfile !== null && companyProfile !== undefined)) &&
          // Entitlements not loaded yet keeps the item visible: the API is the
          // boundary that refuses the call, and hiding on an unknown plan would
          // blank the menu on every first paint.
          (item.feature === undefined ||
            entitlements === null ||
            HasPlanFeature(entitlements, item.feature)),
      ),
    [canManageUsers, companyProfile, entitlements, isPlatformOperator],
  );

  const simplified = entitlements?.usesSimplifiedConsole === true;
  const primaryMenuItems = useMemo(
    () =>
      simplified
        ? visibleMenuItems.filter(
            (item) => item.primaryWhenSimplified === true || item.platformOnly === true,
          )
        : visibleMenuItems,
    [simplified, visibleMenuItems],
  );
  const secondaryMenuItems = useMemo(
    () => (simplified ? visibleMenuItems.filter((item) => !primaryMenuItems.includes(item)) : []),
    [primaryMenuItems, simplified, visibleMenuItems],
  );

  const ProbeSession = useCallback(async (): Promise<void> => {
    setPhase("checking");
    setMessage(null);
    try {
      await client.list({
        assignment: "ALL",
        page: 1,
        pageSize: 1,
        sort: "RECEIVED_DESC",
      });
      setPhase("authenticated");
    } catch (error: unknown) {
      setMessage(SessionProbeMessage(error));
      setPhase(
        error instanceof InternalApiError && error.status === 401 ? "anonymous" : "unavailable",
      );
    }
  }, [client]);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      if (active) {
        void ProbeSession();
      }
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [ProbeSession]);

  useEffect(() => {
    if (phase !== "authenticated") {
      const timer = window.setTimeout(() => {
        setCanManageUsers(false);
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }
    let disposed = false;
    let unsubscribe: (() => void) | undefined;
    void client
      .subscribeRealtimeNotifications(() => {
        setRevision((current) => current + 1);
      })
      .then((stop) => {
        if (disposed) {
          stop();
        } else {
          unsubscribe = stop;
          setRealtimeMessage(null);
        }
      })
      .catch(() => {
        if (!disposed) {
          setRealtimeMessage(
            "La actualización en tiempo real no está disponible. Puedes continuar y actualizar cada sección manualmente.",
          );
        }
      });
    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [client, phase]);

  useEffect(() => {
    if (phase !== "authenticated") {
      return;
    }
    const controller = new AbortController();
    void client
      .listCompanyBranches(controller.signal)
      .then(() => {
        if (!controller.signal.aborted) {
          setCanManageUsers(true);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCanManageUsers(false);
        }
      });
    return () => {
      controller.abort();
    };
  }, [client, phase, revision]);

  useEffect(() => {
    if (phase !== "authenticated") {
      const timer = window.setTimeout(() => {
        setIsPlatformOperator(false);
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }
    const controller = new AbortController();
    void client
      .listPlatformCompanies(controller.signal)
      .then(() => {
        if (!controller.signal.aborted) {
          setIsPlatformOperator(true);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setIsPlatformOperator(false);
        }
      });
    return () => {
      controller.abort();
    };
  }, [client, phase, revision]);

  useEffect(() => {
    if (phase !== "authenticated") {
      const timer = window.setTimeout(() => {
        setEntitlements(null);
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }
    const controller = new AbortController();
    void client
      .getCompanyEntitlements(controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) {
          setEntitlements(loaded);
        }
      })
      .catch(() => {
        // A platform operator has no active company and therefore no plan. The
        // console keeps every section it is otherwise allowed to show.
        if (!controller.signal.aborted) {
          setEntitlements(null);
        }
      });
    return () => {
      controller.abort();
    };
  }, [client, phase, revision]);

  useEffect(() => {
    if (phase !== "authenticated") {
      const timer = window.setTimeout(() => {
        setCompanyProfile(undefined);
        profilePromptedRef.current = false;
      }, 0);
      return () => {
        window.clearTimeout(timer);
      };
    }
    const controller = new AbortController();
    void client
      .getCompanyProfile(controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) {
          setCompanyProfile(loaded);
        }
      })
      .catch(() => {
        // Only the company administrator reads its profile; everyone else (and
        // the platform operator, who has no company) simply gets no prompt.
        if (!controller.signal.aborted) {
          setCompanyProfile(null);
        }
      });
    return () => {
      controller.abort();
    };
  }, [client, phase]);

  const pendingProfileItems = companyProfile?.pending ?? [];
  const hasPendingProfile = pendingProfileItems.length > 0;

  useEffect(() => {
    if (!hasPendingProfile || profilePromptedRef.current) {
      return;
    }
    profilePromptedRef.current = true;
    // Ask right after sign-in, not in the middle of a case someone deep-linked to.
    if (section === "home") {
      void navigate({ replace: true, to: "/company" });
    }
  }, [hasPendingProfile, navigate, section]);

  useEffect(() => {
    if (phase === "authenticated" && !canManageUsers && section === "users") {
      void navigate({ replace: true, to: "/" });
    }
  }, [canManageUsers, navigate, phase, section]);

  const ExpireSession = useCallback((): void => {
    setPhase("anonymous");
    setCanManageUsers(false);
    setIsPlatformOperator(false);
    setEntitlements(null);
    setCompanyProfile(undefined);
    setMessage("La sesión expiró. Vuelve a iniciar sesión.");
    void navigate({ replace: true, to: "/" });
  }, [navigate]);

  const OpenCase = useCallback(
    (publicId: string): void => {
      void navigate({
        params: { publicId },
        to: "/cases/$publicId",
      });
    },
    [navigate],
  );

  const workspaceValue = useMemo(
    () =>
      Object.freeze({
        canManageUsers,
        client,
        companyProfile,
        entitlements,
        expireSession: ExpireSession,
        isPlatformOperator,
        openCase: OpenCase,
        revision,
        setCompanyProfile,
      }),
    [
      ExpireSession,
      OpenCase,
      canManageUsers,
      client,
      companyProfile,
      entitlements,
      isPlatformOperator,
      revision,
    ],
  );

  if (phase === "checking") {
    return (
      <main className="grid min-h-screen place-items-center bg-[var(--color-surface-canvas)] p-6">
        <p className="text-sm font-semibold text-[var(--color-brand-deep)]">
          Verificando la sesión…
        </p>
      </main>
    );
  }

  if (phase === "anonymous") {
    return (
      <>
        {message === null ? null : (
          <p
            className="fixed top-4 left-1/2 z-10 -translate-x-1/2 rounded-[var(--radius-small)] bg-white px-4 py-3 text-sm shadow"
            role="alert"
          >
            {message}
          </p>
        )}
        <LoginPanel
          apiBaseUrl={environment.apiBaseUrl}
          client={client}
          onAuthenticated={() => {
            setMessage(null);
            setPhase("authenticated");
            setRevision((current) => current + 1);
          }}
          onError={setMessage}
        />
      </>
    );
  }

  if (phase === "unavailable") {
    return (
      <main className="grid min-h-screen place-items-center bg-[var(--color-surface-canvas)] p-6">
        <section className="w-full max-w-md rounded-[var(--radius-medium)] border border-[var(--color-border-default)] bg-white p-8 text-center">
          <h1 className="m-0 text-xl font-bold text-[var(--color-brand-deep)]">
            Servicio no disponible
          </h1>
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">{message}</p>
          <ActionButton className="mt-5" onClick={() => void ProbeSession()}>
            Reintentar conexión
          </ActionButton>
        </section>
      </main>
    );
  }

  return (
    <WorkspaceProvider value={workspaceValue}>
      <div className="min-h-screen bg-[var(--color-surface-canvas)] text-[var(--color-text-default)] md:grid md:grid-cols-[13.5rem_minmax(0,1fr)]">
        <aside className="hidden min-h-screen flex-col bg-[var(--color-brand-deep)] p-3 text-white md:sticky md:top-0 md:flex md:h-screen">
          <div className="flex items-center gap-3 px-2 pt-2 pb-6">
            <span aria-hidden="true" className="brand-mark">
              <span />
            </span>
            <div>
              <p className="m-0 text-base font-bold">continnua</p>
              <p className="mt-0.5 mb-0 text-[0.625rem] text-white/60">Libro de Reclamos</p>
            </div>
          </div>
          <nav aria-label="Menú principal" className="grid gap-1">
            {primaryMenuItems.map((item) => (
              <Link
                aria-current={section === item.section ? "page" : undefined}
                className={`rounded-[var(--radius-small)] px-3 py-2.5 text-left transition ${
                  section === item.section
                    ? "bg-[var(--color-brand-primary)] text-white"
                    : "text-white/75 hover:bg-white/10 hover:text-white"
                }`}
                key={item.section}
                to={item.to}
              >
                <span className="block text-sm font-semibold">{item.label}</span>
                <span className="mt-0.5 block text-[0.625rem] opacity-70">{item.description}</span>
              </Link>
            ))}
          </nav>
          {secondaryMenuItems.length === 0 ? null : (
            <nav aria-label="Mi cuenta" className="mt-6 grid gap-1">
              <p className="m-0 px-3 pb-1 text-[0.625rem] font-semibold tracking-wide text-white/50 uppercase">
                Mi cuenta
              </p>
              {secondaryMenuItems.map((item) => (
                <Link
                  aria-current={section === item.section ? "page" : undefined}
                  className={`rounded-[var(--radius-small)] px-3 py-2 text-left text-sm transition ${
                    section === item.section
                      ? "bg-[var(--color-brand-primary)] text-white"
                      : "text-white/70 hover:bg-white/10 hover:text-white"
                  }`}
                  key={item.section}
                  to={item.to}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          )}
          <div className="mt-auto border-t border-white/10 px-2 pt-4 pb-2">
            {entitlements === null ? (
              <p className="m-0 text-xs font-semibold text-white">Equipo interno</p>
            ) : (
              <p className="m-0 text-xs font-semibold text-white">Plan {entitlements.planName}</p>
            )}
            <p className="mt-1 mb-0 text-[0.625rem] text-white/60">Sesión activa</p>
          </div>
        </aside>

        <div className="min-w-0">
          <header className="border-b border-[var(--color-border-default)] bg-white">
            <div className="flex min-h-[3.75rem] items-center justify-between gap-4 px-4 md:px-6">
              <div>
                <p className="m-0 text-xs font-semibold tracking-wide text-[var(--color-brand-primary)] uppercase md:hidden">
                  Libro de Reclamos
                </p>
                <h1 className="m-0 text-base font-bold text-[var(--color-text-default)]">
                  {TitleBySection[section]}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <Suspense fallback={null}>
                  <NotificationBell
                    client={client}
                    onOpenCase={OpenCase}
                    onOpenCenter={() => {
                      void navigate({ to: "/notifications" });
                    }}
                    onSessionExpired={ExpireSession}
                    revision={revision}
                  />
                </Suspense>
                <ActionButton
                  onClick={() => {
                    void Promise.allSettled([client.logout(), client.platformLogout()]).then(
                      ([, platformLogoutResult]) => {
                        setPhase("anonymous");
                        setCanManageUsers(false);
                        setCompanyProfile(undefined);
                        setMessage(null);
                        const logoutUrl =
                          platformLogoutResult.status === "fulfilled"
                            ? platformLogoutResult.value
                            : undefined;
                        if (logoutUrl !== undefined) {
                          // Full-page navigation, not a client-side route: only the browser holds
                          // the Continnua core's own session cookie, so it has to be the one to
                          // visit the core's logout endpoint.
                          window.location.assign(logoutUrl);
                          return;
                        }
                        void navigate({ replace: true, to: "/" });
                      },
                    );
                  }}
                  tone="secondary"
                >
                  Cerrar sesión
                </ActionButton>
              </div>
            </div>
            <nav
              aria-label="Menú principal móvil"
              className="flex gap-1 overflow-x-auto border-t border-[var(--color-border-default)] px-3 py-2 md:hidden"
            >
              {[...primaryMenuItems, ...secondaryMenuItems].map((item) => (
                <Link
                  aria-current={section === item.section ? "page" : undefined}
                  className={`shrink-0 rounded-[var(--radius-small)] px-3 py-2 text-xs font-semibold ${
                    section === item.section
                      ? "bg-[var(--color-brand-primary)] text-white"
                      : "text-[var(--color-brand-deep)]"
                  }`}
                  key={item.section}
                  to={item.to}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </header>

          <main className="p-4 md:p-6">
            {realtimeMessage === null ? null : (
              <p
                className="mt-0 mb-4 rounded-[var(--radius-small)] border border-[var(--color-border-default)] bg-white px-4 py-3 text-sm text-[var(--color-text-muted)]"
                role="status"
              >
                {realtimeMessage}
              </p>
            )}
            {hasPendingProfile && section !== "company" ? (
              <div
                className="mt-0 mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-small)] border border-[color-mix(in_srgb,var(--color-status-warning)_40%,white)] bg-[color-mix(in_srgb,var(--color-status-warning)_8%,white)] px-4 py-3"
                role="status"
              >
                <p className="m-0 text-sm text-[var(--color-text-default)]">
                  {PendingProfileSummary(pendingProfileItems)}
                </p>
                <Link
                  className="shrink-0 text-sm font-semibold text-[var(--color-brand-primary)] underline"
                  to="/company"
                >
                  Completar ahora
                </Link>
              </div>
            ) : null}
            <Outlet />
          </main>
        </div>
      </div>
    </WorkspaceProvider>
  );
}
